import React from 'react';

/**
 * SkeletonLoader - Reusable skeleton loading components
 * Features:
 * - Multiple preset types (table, card, list, detail)
 * - Customizable dimensions
 * - Animated pulse effect
 */

// Base skeleton element with animation
function SkeletonBase({ className = '', style = {} }) {
  return (
    <div
      className={`bg-gray-200 rounded animate-pulse ${className}`}
      style={style}
    />
  );
}

// Text line skeleton
function SkeletonText({ width = '100%', height = '1rem', className = '' }) {
  return (
    <SkeletonBase
      className={className}
      style={{ width, height }}
    />
  );
}

// Circle skeleton (for avatars)
function SkeletonCircle({ size = 40, className = '' }) {
  return (
    <SkeletonBase
      className={`rounded-full ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

// Table row skeleton
function SkeletonTableRow({ columns = 6, className = '' }) {
  return (
    <tr className={className}>
      {Array(columns).fill(0).map((_, i) => (
        <td key={i} className="px-6 py-4">
          <SkeletonText width={i === 0 ? '80%' : '60%'} />
        </td>
      ))}
    </tr>
  );
}

// Full table skeleton
function SkeletonTable({ rows = 5, columns = 6, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Array(columns).fill(0).map((_, i) => (
              <th key={i} className="px-6 py-3">
                <SkeletonText width="70%" height="0.75rem" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array(rows).fill(0).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Card skeleton
function SkeletonCard({ className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center gap-4 mb-4">
        <SkeletonCircle size={48} />
        <div className="flex-1 space-y-2">
          <SkeletonText width="60%" height="1.25rem" />
          <SkeletonText width="40%" height="0.875rem" />
        </div>
      </div>
      <div className="space-y-2">
        <SkeletonText width="100%" />
        <SkeletonText width="90%" />
        <SkeletonText width="75%" />
      </div>
    </div>
  );
}

// Card grid skeleton
function SkeletonCardGrid({ cards = 6, columns = 3, className = '' }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} gap-4 ${className}`}>
      {Array(cards).fill(0).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

// List item skeleton
function SkeletonListItem({ className = '' }) {
  return (
    <div className={`flex items-center gap-4 p-4 border-b border-gray-100 ${className}`}>
      <SkeletonCircle size={40} />
      <div className="flex-1 space-y-2">
        <SkeletonText width="50%" height="1rem" />
        <SkeletonText width="30%" height="0.75rem" />
      </div>
      <SkeletonText width="80px" height="2rem" className="rounded-full" />
    </div>
  );
}

// Full list skeleton
function SkeletonList({ items = 5, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      {Array(items).fill(0).map((_, i) => (
        <SkeletonListItem key={i} />
      ))}
    </div>
  );
}

// Detail view skeleton (for entity detail pages)
function SkeletonDetail({ className = '' }) {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-start gap-6">
          <SkeletonBase className="w-32 h-32 rounded-lg" />
          <div className="flex-1 space-y-3">
            <SkeletonText width="40%" height="2rem" />
            <SkeletonText width="60%" height="1rem" />
            <div className="flex gap-2 mt-4">
              <SkeletonText width="80px" height="1.5rem" className="rounded-full" />
              <SkeletonText width="100px" height="1.5rem" className="rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Info sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2].map(section => (
          <div key={section} className="bg-white rounded-lg shadow-md p-6">
            <SkeletonText width="30%" height="1.25rem" className="mb-4" />
            <div className="space-y-3">
              {[1, 2, 3, 4].map(row => (
                <div key={row} className="flex justify-between">
                  <SkeletonText width="40%" height="0.875rem" />
                  <SkeletonText width="35%" height="0.875rem" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Table section */}
      <SkeletonTable rows={3} columns={4} />
    </div>
  );
}

// Dashboard widget skeleton
function SkeletonWidget({ className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <SkeletonText width="40%" height="1.25rem" />
        <SkeletonCircle size={32} />
      </div>
      <SkeletonText width="60%" height="2.5rem" className="mb-2" />
      <SkeletonText width="80%" height="0.875rem" />
    </div>
  );
}

// Dashboard skeleton
function SkeletonDashboard({ className = '' }) {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* KPI widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array(4).fill(0).map((_, i) => (
          <SkeletonWidget key={i} />
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <SkeletonText width="30%" height="1.25rem" className="mb-4" />
          <SkeletonBase className="w-full h-64" />
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <SkeletonText width="30%" height="1.25rem" className="mb-4" />
          <SkeletonBase className="w-full h-64" />
        </div>
      </div>

      {/* Recent items */}
      <SkeletonList items={4} />
    </div>
  );
}

// Export all components
export {
  SkeletonBase,
  SkeletonText,
  SkeletonCircle,
  SkeletonTableRow,
  SkeletonTable,
  SkeletonCard,
  SkeletonCardGrid,
  SkeletonListItem,
  SkeletonList,
  SkeletonDetail,
  SkeletonWidget,
  SkeletonDashboard
};

// Main component with type selector
function SkeletonLoader({ type = 'table', ...props }) {
  switch (type) {
    case 'table':
      return <SkeletonTable {...props} />;
    case 'card':
      return <SkeletonCard {...props} />;
    case 'cards':
      return <SkeletonCardGrid {...props} />;
    case 'list':
      return <SkeletonList {...props} />;
    case 'detail':
      return <SkeletonDetail {...props} />;
    case 'dashboard':
      return <SkeletonDashboard {...props} />;
    case 'widget':
      return <SkeletonWidget {...props} />;
    default:
      return <SkeletonTable {...props} />;
  }
}

export default SkeletonLoader;
